import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { paymentService } from '../services/payments';
import { documentService } from '../services/documents';
import { changeRequestService } from '../services/changeRequests';
import { memberService } from '../services/members';
import { Payment } from '../types/payment';
import { Document } from '../types/document';
import { ChangeRequest } from '../types/changeRequest';
import { IndividualMemberDto } from '../types/member';
import { MemberDashboardViewModel } from '../types/dashboard';
import { useAuth } from './useAuth';
import { buildMemberDashboardViewModel } from '../utils/dashboardViewModel';

export const useMemberDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [member, setMember] = useState<IndividualMemberDto | null>(null);
  const [viewModel, setViewModel] = useState<MemberDashboardViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setViewModel(null);
      setHasLoaded(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [paymentsData, docs, requests, memberData] = await Promise.all([
        paymentService.getMyPayments().catch(() => []),
        documentService.getMyDocuments().catch(() => []),
        changeRequestService.getMyChangeRequests().catch(() => []),
        memberService.getMyMember().catch(() => null),
      ]);

      setPayments(paymentsData);
      setDocuments(docs);
      setChangeRequests(requests);
      setMember(memberData ?? null);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading member dashboard data:', err);
      setError(i18n.t('dashboard.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, i18n]);

  // Fetch once per user; language changes rebuild the view model below
  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;

    const run = async () => {
      if (!userId) {
        setLoading(false);
        setViewModel(null);
        setHasLoaded(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [paymentsData, docs, requests, memberData] = await Promise.all([
          paymentService.getMyPayments().catch(() => []),
          documentService.getMyDocuments().catch(() => []),
          changeRequestService.getMyChangeRequests().catch(() => []),
          memberService.getMyMember().catch(() => null),
        ]);

        if (cancelled) {
          return;
        }

        setPayments(paymentsData);
        setDocuments(docs);
        setChangeRequests(requests);
        setMember(memberData ?? null);
        setHasLoaded(true);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading member dashboard data:', err);
          setError(i18n.t('dashboard.errors.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the authenticated user changes — not on language/t updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user || !hasLoaded) {
      return;
    }
    setViewModel(
      buildMemberDashboardViewModel(
        {
          user,
          payments,
          documents,
          changeRequests,
          member,
          language: i18n.language,
        },
        t
      )
    );
  }, [user, hasLoaded, payments, documents, changeRequests, member, i18n.language, t]);

  return {
    viewModel,
    payments,
    documents,
    changeRequests,
    member,
    loading,
    error,
    reload,
  };
};
